import { type FormEvent, Fragment, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { AvatarPicker } from '../components/AvatarPicker'
import { CatSharing } from '../components/CatSharing'
import { ConfirmEmailCard } from '../components/ConfirmEmailCard'
import { useToast } from '../lib/toast'
import { Button, ButtonLink, Card, Field, FormError, InfoTip, Label, PageTitle, Toggle } from '../components/ui'
import { ApiError, type Me, type Pet, api, call, messageOf } from '../lib/api'
import { requireUser } from '../lib/auth'
import { type Passkey, addPasskey, deviceName, listPasskeys, passkeysSupported, removePasskey, wasCancelled } from '../lib/passkeys'
import { awaitPicture, meQuery, petsQuery, providersQuery } from '../lib/queries'
import { ago } from '../lib/time'

export const Route = createFileRoute('/account')({
  beforeLoad: ({ context, location }) => requireUser(context.queryClient, location.href),
  component: Account,
})

function Account() {
  const { data: me } = useSuspenseQuery(meQuery)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const signOut = useMutation({
    mutationFn: () => call(api.POST('/auth/logout')),
    onSuccess: () => {
      qc.clear()
      navigate({ to: '/welcome' })
    },
  })
  const home = me.leagues?.[0]
  return (
    <div className="mx-auto grid w-full max-w-[560px] gap-6 px-4 pb-16 pt-[calc(env(safe-area-inset-top,0px)+20px)]">
      <div className="flex items-center justify-between gap-3">
        {home ? (
          <Link to="/l/$slug" params={{ slug: home.slug }} search={{ category: undefined }} className="text-[14px] font-semibold text-muted">
            ← {home.name}
          </Link>
        ) : (
          <span />
        )}
        <Button size="sm" id="sign-out" busy={signOut.isPending} onClick={() => signOut.mutate()}>Sign out</Button>
      </div>
      <Me username={me.user.username} name={me.user.display_name} avatar={me.user.avatar_url} hasGoogle={me.has_google} googlePicture={me.google_picture} />
      <ConfirmEmailCard />
      <Cats />
      <Profile name={me.user.display_name} username={me.user.username} />
      <Notifications rows={me.notifications} name={me.user.display_name} />
      {passkeysSupported() && <Passkeys />}
      <Password hasPassword={me.user.has_password} hasGoogle={me.has_google} hasApple={me.has_apple} />
      {(me.leagues ?? []).length > 0 && (
        <section className="grid gap-2">
          <Label>Your leagues</Label>
          {(me.leagues ?? []).map((l) => (
            <Link key={l.slug} to="/l/$slug" params={{ slug: l.slug }} search={{ category: undefined }} className="flex items-center justify-between rounded-card border border-line bg-surface px-4 py-3 text-[15px]">
              <b className="font-semibold">{l.name}</b>
              <span className="text-[13px] text-muted">{l.member_count === 1 ? '1 manager' : `${l.member_count} managers`}</span>
            </Link>
          ))}
        </section>
      )}
      <div className="flex flex-wrap gap-2.5">
        <ButtonLink to="/start" size="sm">Start or join another league</ButtonLink>
        <ButtonLink to="/about" size="sm">About</ButtonLink>
        <ButtonLink to="/privacy" size="sm">Privacy</ButtonLink>
      </div>
      <Blocked />
      <DeleteAccount />
    </div>
  )
}

/** The members you've blocked (D41), each with a way back. Absent when
 *  there are none: most people never block anyone. */
function Blocked() {
  const qc = useQueryClient()
  const toast = useToast()
  const blocks = useQuery({ queryKey: ['blocks'], queryFn: async () => (await call(api.GET('/me/blocks'))).blocked ?? [] })
  const unblock = useMutation({
    mutationFn: (id: number) => call(api.DELETE('/users/{id}/block', { params: { path: { id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocks'] })
      qc.invalidateQueries({ queryKey: ['feed'] })
      qc.invalidateQueries({ queryKey: ['ballot'] })
      qc.invalidateQueries({ queryKey: ['league'] })
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  const list = blocks.data ?? []
  if (list.length === 0) return null
  return (
    <section id="blocked" className="grid gap-2.5 border-t border-line pt-5">
      <Label>Blocked</Label>
      <p className="text-[14.5px] text-muted">You don't see their posts. Unblock someone to see them again.</p>
      {list.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3 rounded-card border border-line bg-surface px-4 py-3">
          <div className="min-w-0">
            <b className="block truncate text-[15px] font-semibold">{p.display_name}</b>
            <span className="text-[13px] text-muted">@{p.username}</span>
          </div>
          <Button size="sm" busy={unblock.isPending && unblock.variables === p.id} onClick={() => unblock.mutate(p.id)}>
            Unblock
          </Button>
        </div>
      ))}
    </section>
  )
}

function DeleteAccount() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [sure, setSure] = useState(false)
  const del = useMutation({
    mutationFn: () => call(api.DELETE('/me')),
    onSuccess: () => {
      qc.clear()
      toast('Your account is gone. Thanks for playing.')
      navigate({ to: '/welcome' })
    },
  })
  return (
    <section id="delete-account" className="grid gap-2.5 border-t border-line pt-5">
      <Label>Delete account</Label>
      <p className="text-[14.5px] text-muted">
        Removes your account, your cats, and every photo and video you posted, from our servers too. Your leagues keep going without you; the points other people earned stay.
      </p>
      <FormError>{del.isError && messageOf(del.error)}</FormError>
      {sure ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[14.5px]">Delete your account? This can't be undone.</span>
          <Button variant="danger" size="sm" busy={del.isPending} onClick={() => del.mutate()}>Yes, delete everything</Button>
          <Button size="sm" onClick={() => setSure(false)}>Keep it</Button>
        </div>
      ) : (
        <Button variant="danger" size="sm" onClick={() => setSure(true)} className="justify-self-start">Delete my account</Button>
      )}
    </section>
  )
}

function Me({ username, name, avatar, hasGoogle, googlePicture }: { username: string; name: string; avatar?: string | null; hasGoogle: boolean; googlePicture?: string | null }) {
  const qc = useQueryClient()
  const toast = useToast()
  const refresh = () => qc.invalidateQueries()
  const remove = useMutation({
    mutationFn: () => call(api.DELETE('/me/avatar')),
    onSuccess: refresh,
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  const [copying, setCopying] = useState(false)
  const fromGoogle = useMutation({
    mutationFn: () => call(api.POST('/me/avatar/google')),
    onSuccess: async () => {
      // The copy is made in the background and lands within seconds; the
      // page says so and watches for it (sc-2381).
      setCopying(true)
      const landed = await awaitPicture(qc, avatar)
      setCopying(false)
      if (!landed) toast("Your Google picture hasn't arrived yet. Give it a minute and reload.", 'bad')
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  // sc-288: with no address recorded yet (Google connected before we kept
  // one), a Google sign-in records it and brings them back here. The link is
  // offered only while there is no picture (sc-2381: once the Google picture
  // is in place, offering it again reads as if it hadn't worked); with a
  // picture, Remove comes first.
  const link = 'font-semibold underline underline-offset-2'
  const google = copying ? (
    <span>Copying your Google picture…</span>
  ) : avatar ? null : googlePicture ? (
    <button type="button" className={link} disabled={fromGoogle.isPending} onClick={() => fromGoogle.mutate()}>
      Use my Google picture
    </button>
  ) : hasGoogle ? (
    <a className={link} href="/api/auth/google/start?next=%2Faccount">
      Get my Google picture
    </a>
  ) : null
  return (
    <div className="flex items-center gap-4">
      <AvatarPicker
        kind="person"
        size="lg"
        src={avatar}
        label="Change your picture"
        onPicked={async (id) => {
          await call(api.PUT('/me/avatar', { body: { media_id: id } }))
          await refresh()
        }}
      />
      <div className="grid min-w-0 gap-1">
        <PageTitle eyebrow={`@${username}`}>{name}</PageTitle>
        <p className="text-[13px] text-muted">
          Tap the picture to change it.
          {avatar && (
            <>
              {' '}
              <button type="button" className={link} disabled={remove.isPending} onClick={() => remove.mutate()}>
                Remove
              </button>
            </>
          )}
        </p>
        {google && <p className="text-[13px] text-muted">{google}</p>}
      </div>
    </div>
  )
}

function Cats() {
  const qc = useQueryClient()
  const toast = useToast()
  const pets = useQuery(petsQuery)
  const [name, setName] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: petsQuery.queryKey })
  const add = useMutation({
    mutationFn: () => call(api.POST('/pets', { body: { name } })),
    onSuccess: (p) => {
      setName('')
      refresh()
      toast(`${p.name} added`)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    add.mutate()
  }
  return (
    <section className="grid gap-3">
      <Label>Your cats</Label>
      {pets.data?.length === 0 && <p className="text-[14.5px] text-muted">Add the cats you'll be entering. Every post is tagged with one.</p>}
      <ul className="grid gap-4">{(pets.data ?? []).map((p) => <CatRow key={p.id} pet={p} onChange={refresh} />)}</ul>
      <form onSubmit={submit} className="grid grid-cols-[1fr_auto] items-end gap-2.5">
        <Field label="Add a cat" required maxLength={40} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" variant="ink" busy={add.isPending}>Add</Button>
      </form>
      <FormError>{add.isError && messageOf(add.error)}</FormError>
    </section>
  )
}

function CatRow({ pet, onChange }: { pet: Pet; onChange: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(pet.name)
  const rename = useMutation({
    mutationFn: () => call(api.PATCH('/pets/{id}', { params: { path: { id: pet.id } }, body: { name } })),
    onSuccess: () => {
      setEditing(false)
      onChange()
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  const remove = useMutation({
    mutationFn: () => call(api.DELETE('/pets/{id}', { params: { path: { id: pet.id } } })),
    onSuccess: onChange,
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  if (editing) {
    return (
      <li>
        <form onSubmit={(e) => { e.preventDefault(); rename.mutate() }} className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
          <Field label="Name" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="ink" busy={rename.isPending}>Save</Button>
          <Button onClick={() => { setEditing(false); setName(pet.name) }}>Cancel</Button>
        </form>
      </li>
    )
  }
  return (
    <li>
      <Card className="flex items-center justify-between gap-3 !py-3">
        <span className="flex min-w-0 items-center gap-3">
          <AvatarPicker
            kind="cat"
            src={pet.photo?.thumb_url}
            label={`Change ${pet.name}'s picture`}
            onPicked={async (id) => {
              await call(api.PATCH('/pets/{id}', { params: { path: { id: pet.id } }, body: { name: pet.name, photo_media_id: id } }))
              qc.invalidateQueries()
            }}
          />
          <b className="truncate text-[16px] font-semibold">{pet.name}</b>
        </span>
        <span className="flex gap-2">
          <Button size="sm" onClick={() => setEditing(true)}>Rename</Button>
          <Button size="sm" variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
        </span>
      </Card>
      <div className="mt-2"><CatSharing pet={pet} /></div>
    </li>
  )
}

const channelNames: Record<string, string> = { email: 'Email', push: 'Push', web: 'In the app' }
const channelName = (c: string) => channelNames[c] ?? c

/** The notification settings grid (designs/20261019-notification-settings.md
 *  §5): a row per category the server shows this person, its label and an
 *  info button listing the events in it, then a switch per built channel.
 *  Rows, labels and defaults all come from GET /me; a change saves at once. */
function Notifications({ rows, name }: { rows: Me['notifications']; name: string }) {
  const qc = useQueryClient()
  const toast = useToast()
  const set = useMutation({
    mutationFn: (s: { category: string; channel: string; on: boolean; label: string }) =>
      call(api.PATCH('/me', { body: { display_name: name, notifications: [{ category: s.category, channel: s.channel, on: s.on }] } })),
    onSuccess: (me, s) => {
      qc.setQueryData(meQuery.queryKey, me)
      toast(`${s.label} ${channelName(s.channel).toLowerCase()} ${s.on ? 'on' : 'off'}`)
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  const list = rows ?? []
  if (list.length === 0) return null
  const channels = (list[0].channels ?? []).map((c) => c.channel)
  return (
    <section id="notifications" className="grid gap-3 border-t border-line pt-5">
      <div className="grid items-center gap-x-4 gap-y-3" style={{ gridTemplateColumns: `1fr repeat(${channels.length}, auto)` }}>
        <Label>Notifications</Label>
        {channels.map((c) => <span key={c} className="text-center text-[13px] font-semibold text-muted">{channelName(c)}</span>)}
        {list.map((r) => (
          <Fragment key={r.category}>
            <span className="flex items-center gap-0.5">
              <span className="text-[14.5px] font-semibold">{r.label}</span>
              {(r.events ?? []).length > 0 && (
                <InfoTip label={`What's in ${r.label}`}>
                  <ul className="grid gap-1">{(r.events ?? []).map((e) => <li key={e}>{e}</li>)}</ul>
                </InfoTip>
              )}
            </span>
            {(r.channels ?? []).map((c) => (
              <Toggle key={c.channel} id={`notify-${r.category}-${c.channel}`} label={`${r.label}, ${channelName(c.channel)}`} labelHidden checked={c.on}
                onChange={(on) => set.mutate({ category: r.category, channel: c.channel, on, label: r.label })} />
            ))}
          </Fragment>
        ))}
      </div>
    </section>
  )
}

function Passkeys() {
  const qc = useQueryClient()
  const toast = useToast()
  const keys = useQuery({ queryKey: ['passkeys'], queryFn: listPasskeys })
  const refresh = () => qc.invalidateQueries({ queryKey: ['passkeys'] })
  const add = useMutation({
    mutationFn: () => addPasskey(deviceName()),
    onSuccess: (p) => {
      refresh()
      toast(`Passkey added for ${p.name}`)
    },
    onError: (e) => !wasCancelled(e) && toast(messageOf(e), 'bad'),
  })
  const remove = useMutation({
    mutationFn: (p: Passkey) => removePasskey(p.id),
    onSuccess: () => {
      refresh()
      toast('Passkey removed')
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  return (
    <section className="grid gap-3 border-t border-line pt-5">
      <Label>Passkeys</Label>
      <p className="text-[14.5px] text-muted">Sign in with Face ID, Touch ID or your device PIN. Nothing to remember, and nothing that can be phished.</p>
      <ul className="grid gap-2">
        {(keys.data ?? []).map((p) => (
          <li key={p.id}>
            <Card className="flex items-center justify-between gap-3 !py-3">
              <span className="min-w-0">
                <b className="block truncate text-[15.5px] font-semibold">{p.name}</b>
                <span className="text-[12.5px] text-muted">
                  {p.last_used_at ? `Used ${ago(p.last_used_at)}` : `Added ${ago(p.created_at)}`}
                  {p.synced ? ', synced' : ''}
                </span>
              </span>
              <Button size="sm" variant="danger" busy={remove.isPending && remove.variables?.id === p.id} onClick={() => remove.mutate(p)}>Remove</Button>
            </Card>
          </li>
        ))}
      </ul>
      <Button id="passkey-add" busy={add.isPending} onClick={() => add.mutate()} className="justify-self-start">Add a passkey</Button>
    </section>
  )
}

function Profile({ name, username }: { name: string; username: string }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [display, setDisplay] = useState(name)
  const [handle, setHandle] = useState(username)
  const save = useMutation({
    mutationFn: () => call(api.PATCH('/me', { body: { display_name: display, username: handle } })),
    onSuccess: (me) => {
      qc.setQueryData(meQuery.queryKey, me)
      toast('Profile saved')
    },
  })
  const fields = save.error instanceof ApiError ? save.error.fields : {}
  const unchanged = display.trim() === name && handle.trim() === username
  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate() }} className="grid gap-3 border-t border-line pt-5">
      <Label>Profile</Label>
      <FormError>{save.isError && messageOf(save.error)}</FormError>
      <Field label="Your name" name="name" autoComplete="name" required maxLength={60} value={display} onChange={(e) => setDisplay(e.target.value)} error={fields.display_name} />
      <Field label="Username" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required minLength={3} maxLength={32} pattern="[A-Za-z0-9_]+" value={handle} onChange={(e) => setHandle(e.target.value)} error={fields.username} help="What you sign in with, and how you appear as @name." />
      <Button type="submit" busy={save.isPending} disabled={unchanged} className="justify-self-start">Save profile</Button>
    </form>
  )
}

/** The ways in without a password are named here, so an Apple account is
 *  told it signs in with Apple, not Google (sc-702). */
function signInsName(hasGoogle: boolean, hasApple: boolean): string {
  const ways = [hasGoogle && 'Google', hasApple && 'Apple'].filter((w): w is string => !!w)
  if (ways.length === 0) return 'a passkey'
  return ways.join(' and ')
}

function Password({ hasPassword, hasGoogle, hasApple }: { hasPassword: boolean; hasGoogle: boolean; hasApple: boolean }) {
  const providers = useQuery(providersQuery)
  const qc = useQueryClient()
  const toast = useToast()
  const [f, setF] = useState({ current_password: '', new_password: '' })
  const save = useMutation({
    mutationFn: () => call(api.POST('/auth/password', { body: f })),
    onSuccess: () => {
      setF({ current_password: '', new_password: '' })
      qc.invalidateQueries({ queryKey: meQuery.queryKey })
      toast(hasPassword ? 'Password changed. Other devices were signed out.' : 'Password set')
    },
  })
  return (
    <form id="change-password" onSubmit={(e) => { e.preventDefault(); save.mutate() }} className="grid gap-3 border-t border-line pt-5">
      <Label>Password</Label>
      {(hasGoogle || hasApple) && (
        <p className="text-[14.5px] text-muted" id="connected-sign-ins">
          Connected: {signInsName(hasGoogle, hasApple)}.
        </p>
      )}
      {!hasApple && providers.data?.apple_web && (
        <a className="justify-self-start text-[14.5px] font-semibold text-accent" href="/api/auth/apple/start?connect=1&next=%2Faccount">
          Connect Apple
        </a>
      )}
      {!hasPassword && <p className="text-[14.5px] text-muted">You sign in with {signInsName(hasGoogle, hasApple)}. Add a password if you'd like a second way in.</p>}
      <FormError>{save.isError && messageOf(save.error)}</FormError>
      {hasPassword && <Field label="Current password" name="current-password" type="password" autoComplete="current-password" required value={f.current_password} onChange={(e) => setF((s) => ({ ...s, current_password: e.target.value }))} />}
      <Field label={hasPassword ? 'New password' : 'Password'} name="new-password" type="password" autoComplete="new-password" required minLength={10} maxLength={200} value={f.new_password} onChange={(e) => setF((s) => ({ ...s, new_password: e.target.value }))} help="Ten characters or more." />
      <Button type="submit" busy={save.isPending} className="justify-self-start">{hasPassword ? 'Change password' : 'Set password'}</Button>
    </form>
  )
}
